import { narrativeStructure } from '../data/narrativeStructure';
import {
  CompletionStatus,
  NarrativeState,
  Node,
  SegmentId,
  VariableContent,
} from '../types/narrative';

// Memoization cache for completion calculations
const completionCache = new Map<string, CompletionStatus>();
const segmentCompletionCache = new Map<string, CompletionStatus>();

// Generate cache key for node completion
function getNodeCompletionCacheKey(
  node: Node,
  nodeId: string,
  narrativeState?: NarrativeState
): string {
  const stateHash = narrativeState
    ? JSON.stringify({
        variables: narrativeState.variables,
        nodeText: node.text,
        choices: node.choices,
        variantContent: node.variantContent,
        textMainGoodWorld: node.textMainGoodWorld,
        textMainBadWorld: node.textMainBadWorld,
        textBeforeGoodWorld: node.textBeforeGoodWorld,
        textAfterGoodWorld: node.textAfterGoodWorld,
        textBeforeBadWorld: node.textBeforeBadWorld,
        textAfterBadWorld: node.textAfterBadWorld,
        choicesGoodWorld: node.choicesGoodWorld,
        choicesBadWorld: node.choicesBadWorld,
      })
    : 'no-state';
  return `${nodeId}-${stateHash}`;
}

// Generate cache key for segment completion
function getSegmentCompletionCacheKey(state: NarrativeState, segmentId: string): string {
  const segmentNodes = Object.values(state.nodes).filter(
    (node) => node.segmentId.toString() === segmentId.toString()
  );
  const completionStatuses = segmentNodes
    .map((node) => `${node.id}:${node.completionStatus}`)
    .join(',');
  return `${segmentId}-${completionStatuses}`;
}

// Clear completion caches (useful when major state changes occur)
export function clearCompletionCaches(): void {
  completionCache.clear();
  segmentCompletionCache.clear();
}

// Check if a single field is filled
function isFieldFilled(field: string | undefined): boolean {
  return field !== undefined && field.trim().length > 0;
}

// Check if a variable content is filled
function isVariableContentFilled(content: VariableContent | undefined): boolean {
  if (!content) return false;
  return isFieldFilled(content.playerFacingText) && isFieldFilled(content.memoryContent);
}

// Calculate completion status for a standard node
function calculateStandardNodeCompletion(node: Node, nodeId: string): CompletionStatus {
  const hasText = isFieldFilled(node.text);
  const choicesComplete =
    node.choices?.every((choice) => {
      const hasChoiceText = isFieldFilled(choice.text);
      const hasNpcResponse =
        choice.npcResponse !== undefined ? isFieldFilled(choice.npcResponse) : true;

      // For S1_Player_Declaration, the choice text itself serves as the variable
      // No need to check for additional variable content fields
      const hasVariableContent =
        nodeId === 'S1_Player_Declaration'
          ? true
          : choice.setsVariable?.value
            ? typeof choice.setsVariable.value === 'object'
              ? isVariableContentFilled(choice.setsVariable.value as VariableContent)
              : true
            : true;

      const isChoiceComplete = hasChoiceText && hasNpcResponse && hasVariableContent;

      return isChoiceComplete;
    }) ?? true;

  const status =
    hasText && choicesComplete
      ? 'complete'
      : hasText || (node.choices?.some((c) => isFieldFilled(c.text)) ?? false)
        ? 'partial'
        : 'empty';

  return status;
}

// Calculate completion status for conditional node (world-variant nodes)
function calculateConditionalNodeCompletion(
  node: Node,
  _narrativeState?: NarrativeState
): CompletionStatus {
  // For world-variant nodes, check the appropriate world's variant content
  const worldFromNodeId = getWorldFromNodeId(node.id);

  if (worldFromNodeId) {
    const worldKey = worldFromNodeId === 'good' ? 'goodWorld' : 'badWorld';
    const currentWorldVariants = node.variantContent?.[worldKey]?.variants || [];

    // Check main text
    const hasMainText = isFieldFilled(node.text);

    // Check variant content - all variant lines must be filled if any exist
    const hasAllVariantContent =
      currentWorldVariants.length > 0
        ? currentWorldVariants.every((v) => isFieldFilled(v.variantLine))
        : false;

    // Check choices
    const hasAllChoices = node.choices?.every((choice) => isFieldFilled(choice.text)) ?? false;

    // For completion, need main text, all variants (if any), and all choices
    if (
      hasMainText &&
      hasAllChoices &&
      (currentWorldVariants.length === 0 || hasAllVariantContent)
    ) {
      return 'complete';
    }

    // Check if has any content
    const hasAnyContent =
      hasMainText ||
      (node.choices?.some((c) => isFieldFilled(c.text)) ?? false) ||
      currentWorldVariants.some((v) => isFieldFilled(v.variantLine));

    if (hasAnyContent) return 'partial';
  }

  return 'empty';
}

// Calculate node completion status (with memoization)
export function calculateNodeCompletion(
  node: Node,
  nodeId: string,
  narrativeState?: NarrativeState
): CompletionStatus {
  if (node.type === 'Structural' || node.type === 'Terminal') {
    return 'complete'; // These nodes have no content to fill
  }

  // Check cache first
  const cacheKey = getNodeCompletionCacheKey(node, nodeId, narrativeState);
  if (completionCache.has(cacheKey)) {
    return completionCache.get(cacheKey)!;
  }

  // Calculate completion
  let result: CompletionStatus;
  if (node.type === 'Conditional') {
    result = calculateConditionalNodeCompletion(node, narrativeState);
  } else {
    result = calculateStandardNodeCompletion(node, nodeId);
  }

  // Cache the result
  completionCache.set(cacheKey, result);

  // Prevent cache from growing too large
  if (completionCache.size > 1000) {
    const firstKey = completionCache.keys().next().value;
    if (firstKey) {
      completionCache.delete(firstKey);
    }
  }

  return result;
}

// Get nodes for a specific segment, handling world variants
function getNodesForSegment(state: NarrativeState, segmentId: string) {
  if (segmentId === '3A') {
    // Get only Better World nodes
    return Object.values(state.nodes).filter(
      (node) =>
        node.id.startsWith('S3A_') ||
        (node.segmentId === '3A' && !node.id.startsWith('S3A_') && !node.id.startsWith('S3B_'))
    );
  } else if (segmentId === '3B') {
    // Get only Worse World nodes
    return Object.values(state.nodes).filter(
      (node) =>
        node.id.startsWith('S3B_') ||
        (node.segmentId === '3B' && !node.id.startsWith('S3A_') && !node.id.startsWith('S3B_'))
    );
  } else {
    // Standard segment filtering
    return Object.values(state.nodes).filter(
      (node) => node.segmentId.toString() === segmentId.toString()
    );
  }
}

// Check if all nodes in a segment are complete
export function isSegmentComplete(state: NarrativeState, segmentId: string): boolean {
  const segmentNodes = getNodesForSegment(state, segmentId);
  return segmentNodes.every((node) => node.completionStatus === 'complete');
}

// Get segment completion status (with memoization)
export function getSegmentCompletion(state: NarrativeState, segmentId: string): CompletionStatus {
  // Check cache first
  const cacheKey = getSegmentCompletionCacheKey(state, segmentId);
  if (segmentCompletionCache.has(cacheKey)) {
    return segmentCompletionCache.get(cacheKey)!;
  }

  // Calculate segment completion
  const segmentNodes = getNodesForSegment(state, segmentId);

  const hasComplete = segmentNodes.some((node) => node.completionStatus === 'complete');
  const hasPartial = segmentNodes.some((node) => node.completionStatus === 'partial');
  const allComplete = segmentNodes.every((node) => node.completionStatus === 'complete');

  let result: CompletionStatus;
  if (allComplete) result = 'complete';
  else if (hasComplete || hasPartial) result = 'partial';
  else result = 'empty';

  // Cache the result
  segmentCompletionCache.set(cacheKey, result);

  // Prevent cache from growing too large
  if (segmentCompletionCache.size > 100) {
    const firstKey = segmentCompletionCache.keys().next().value;
    if (firstKey) {
      segmentCompletionCache.delete(firstKey);
    }
  }

  return result;
}

export function getSegmentProgress(
  state: NarrativeState,
  segmentId: SegmentId
): { complete: number; total: number } {
  const segmentNodes = getNodesForSegment(state, segmentId.toString());
  const completeNodes = segmentNodes.filter((node) => node.completionStatus === 'complete').length;
  return { complete: completeNodes, total: segmentNodes.length };
}

function areSegmentsAlwaysUnlocked(): boolean {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('UNLOCK_SEGMENTS');
    return stored === null ? true : stored === 'true';
  }
  return import.meta.env.VITE_UNLOCK_SEGMENTS === 'true';
}

export function toggleSegmentUnlock(): void {
  if (typeof window !== 'undefined') {
    const current = areSegmentsAlwaysUnlocked();
    localStorage.setItem('UNLOCK_SEGMENTS', String(!current));
    console.log(`Segment unlocking ${!current ? 'enabled' : 'disabled'}. Refresh to apply.`);
  }
}

if (typeof window !== 'undefined') {
  (window as any).toggleSegmentUnlock = toggleSegmentUnlock;
}

// Check if a segment is unlocked
export function isSegmentUnlocked(state: NarrativeState, segmentId: string): boolean {
  if (areSegmentsAlwaysUnlocked()) {
    return true;
  }
  switch (segmentId) {
    case '1':
      return true; // Always unlocked
    case '2':
      return isSegmentComplete(state, '1');
    case '3A':
    case '3B':
      return isSegmentComplete(state, '2');
    default:
      return false;
  }
}

// Initialize narrative state from structure
export function initializeNarrativeState(): NarrativeState {
  const nodes: Record<string, Node> = {};

  // Create nodes with completion status
  Object.entries(narrativeStructure).forEach(([id, structureNode]) => {
    nodes[id] = {
      ...structureNode,
      completionStatus: calculateNodeCompletion(structureNode as Node, id),
    };
  });

  const state: NarrativeState = {
    variables: {
      player_node2_statement_for_npc_memory: '',
    },
    nodes,
    currentSegmentId: 1,
    currentNodeId: 'S1_Opening_Hook',
    segmentCompletion: {
      1: 'empty',
      2: 'empty',
      '3A': 'empty',
      '3B': 'empty',
    },
  };

  // Update segment completion
  state.segmentCompletion[1] = getSegmentCompletion(state, '1');
  state.segmentCompletion[2] = getSegmentCompletion(state, '2');
  state.segmentCompletion['3A'] = getSegmentCompletion(state, '3A');
  state.segmentCompletion['3B'] = getSegmentCompletion(state, '3B');

  return state;
}

// Update node content and recalculate completion
export function updateNodeContent(
  state: NarrativeState,
  nodeId: string,
  updates: Partial<Node>
): NarrativeState {
  const originalNode = state.nodes[nodeId];
  if (!originalNode) {
    // This case should ideally not be reached if nodeId is always valid
    console.error(`[narrativeHelpers] updateNodeContent: Node with ID ${nodeId} not found.`);
    return state;
  }

  // Create the updated version of the specific node
  const updatedNode = { ...originalNode, ...updates };

  // Recalculate completion status for the updated node
  updatedNode.completionStatus = calculateNodeCompletion(updatedNode, nodeId, state);

  // Create a new 'nodes' object with the updated node
  const newNodes = {
    ...state.nodes,
    [nodeId]: updatedNode,
  };

  // Create the base new narrativeState
  let newNarrativeState: NarrativeState = {
    ...state,
    nodes: newNodes,
  };

  // Recalculate and update segment completion using the newNarrativeState
  const segmentId = updatedNode.segmentId;
  const newSegmentCompletion = { ...newNarrativeState.segmentCompletion };
  newSegmentCompletion[segmentId] = getSegmentCompletion(newNarrativeState, segmentId.toString());

  newNarrativeState = {
    ...newNarrativeState,
    segmentCompletion: newSegmentCompletion,
  };

  return newNarrativeState;
}

// Get all ancestor nodes for a given node
export function getAncestorNodes(state: NarrativeState, nodeId: string): Node[] {
  const ancestors: Node[] = [];
  const visited = new Set<string>();

  function traverse(currentId: string) {
    if (visited.has(currentId)) return;
    visited.add(currentId);

    const node = state.nodes[currentId];
    if (!node || !node.parentIds) return;

    node.parentIds.forEach((parentId) => {
      const parent = state.nodes[parentId];
      if (parent) {
        ancestors.push(parent);
        traverse(parentId);
      }
    });
  }

  traverse(nodeId);
  return ancestors;
}

// Get sibling nodes (nodes that share a parent with the current node)
export function getSiblingNodes(state: NarrativeState, nodeId: string): Node[] {
  const node = state.nodes[nodeId];
  if (!node || !node.parentIds || node.parentIds.length === 0) return [];

  const siblings = new Set<Node>();

  node.parentIds.forEach((parentId) => {
    const parent = state.nodes[parentId];
    if (parent && parent.childIds) {
      parent.childIds.forEach((childId) => {
        if (childId !== nodeId) {
          const sibling = state.nodes[childId];
          if (sibling) siblings.add(sibling);
        }
      });
    }
  });

  return Array.from(siblings);
}

// Get sibling nodes that belong to a specific segment
export function getSiblingNodesInSegment(
  state: NarrativeState,
  nodeId: string,
  segmentId: SegmentId
): Node[] {
  return getSiblingNodes(state, nodeId).filter((sibling) => sibling.segmentId === segmentId);
}

// Get the starting node for a segment
export function getSegmentStartNode(segmentId: SegmentId): string {
  const startNodes: Record<SegmentId, string> = {
    1: 'S1_Opening_Hook',
    2: 'S2_BPlot_Intro',
    '3A': 'S3A_NPC_Recall_Positive',
    '3B': 'S3B_NPC_Recall_Negative',
  };
  return startNodes[segmentId];
}

// Check if the current branch is complete for segment 3 final nodes
export function isCurrentBranchComplete(state: NarrativeState, nodeId: string): boolean {
  // Only apply to the final nodes in segment 3A and 3B
  const finalNodesSegment3 = [
    'S3A_Silent_Intensify_A',
    'S3A_Silent_Intensify_B',
    'S3B_Silent_Intensify_A',
    'S3B_Silent_Intensify_B',
  ];

  if (!finalNodesSegment3.includes(nodeId)) {
    return false;
  }

  const node = state.nodes[nodeId];
  if (!node) return false;

  // Check if the current branch (from segment start to this node) is complete
  // Get all nodes in the same segment that are reachable from the segment start
  const segmentId = node.segmentId;
  const segmentStart = getSegmentStartNode(segmentId);

  // Get all nodes in this specific branch path
  const branchNodes = getReachableNodesInBranch(state, segmentStart, nodeId);

  // Check if all nodes in this branch are complete
  return branchNodes.every((branchNodeId) => {
    const branchNode = state.nodes[branchNodeId];
    return branchNode && branchNode.completionStatus === 'complete';
  });
}

// Helper function to get all nodes reachable in a specific branch
function getReachableNodesInBranch(
  state: NarrativeState,
  startNodeId: string,
  targetNodeId: string
): string[] {
  const visited = new Set<string>();
  const path: string[] = [];

  function findPath(currentNodeId: string): boolean {
    if (visited.has(currentNodeId)) return false;
    visited.add(currentNodeId);
    path.push(currentNodeId);

    if (currentNodeId === targetNodeId) return true;

    const currentNode = state.nodes[currentNodeId];
    if (!currentNode || !currentNode.childIds) {
      path.pop();
      return false;
    }

    // Try each child path
    for (const childId of currentNode.childIds) {
      if (findPath(childId)) return true;
    }

    path.pop();
    return false;
  }

  findPath(startNodeId);
  return path;
}

// Clear editable content of a node
export function clearNodeContent(state: NarrativeState, nodeId: string): NarrativeState {
  const newState = { ...state };
  const node = { ...newState.nodes[nodeId] };
  if (!node) return state;

  if (node.text !== undefined) node.text = '';
  if (node.choices) {
    node.choices = node.choices.map((c) => ({
      ...c,
      text: '',
      npcResponse: c.npcResponse !== undefined ? '' : undefined,
      setsVariable: c.setsVariable
        ? {
            ...c.setsVariable,
            value:
              typeof c.setsVariable.value === 'object'
                ? { playerFacingText: '', memoryContent: '' }
                : '',
          }
        : undefined,
    }));
  }
  if (node.conditionalTextBlocks) {
    node.conditionalTextBlocks = {
      goodWorld: node.conditionalTextBlocks.goodWorld.map((b) => ({ ...b, textRecall: '' })),
      badWorld: node.conditionalTextBlocks.badWorld.map((b) => ({ ...b, textRecall: '' })),
    };
  }
  // For the final sequence memory nodes, clear the split fields
  if (
    nodeId === 'FinalSequence_Memory_Node' ||
    nodeId === 'S3A_NPC_Recall_Positive' ||
    nodeId === 'S3B_NPC_Recall_Negative'
  ) {
    if (node.textBeforeGoodWorld !== undefined) node.textBeforeGoodWorld = '';
    if (node.textAfterGoodWorld !== undefined) node.textAfterGoodWorld = '';
    if (node.textBeforeBadWorld !== undefined) node.textBeforeBadWorld = '';
    if (node.textAfterBadWorld !== undefined) node.textAfterBadWorld = '';
  }
  // Clear old combined fields for backwards compatibility
  if (node.textMainGoodWorld !== undefined) {
    node.textMainGoodWorld = '';
  }
  if (node.textMainBadWorld !== undefined) {
    node.textMainBadWorld = '';
  }
  if (node.choicesGoodWorld) {
    node.choicesGoodWorld = node.choicesGoodWorld.map((c) => ({ ...c, text: '' }));
  }
  if (node.choicesBadWorld) {
    node.choicesBadWorld = node.choicesBadWorld.map((c) => ({ ...c, text: '' }));
  }
  if (node.variantContent) {
    node.variantContent = {
      ...node.variantContent,
      goodWorld: node.variantContent.goodWorld
        ? {
            ...node.variantContent.goodWorld,
            variants: node.variantContent.goodWorld.variants.map((v) => ({
              ...v,
              variantLine: '',
            })),
          }
        : undefined,
      badWorld: node.variantContent.badWorld
        ? {
            ...node.variantContent.badWorld,
            variants: node.variantContent.badWorld.variants.map((v) => ({ ...v, variantLine: '' })),
          }
        : undefined,
    };
  }

  newState.nodes[nodeId] = node;
  newState.nodes[nodeId].completionStatus = calculateNodeCompletion(node, nodeId, newState);
  newState.segmentCompletion[node.segmentId] = getSegmentCompletion(
    newState,
    node.segmentId.toString()
  );

  return newState;
}

// Determine which world variant a node belongs to based on its ID
export function getWorldFromNodeId(nodeId: string): 'good' | 'bad' | null {
  if (nodeId.startsWith('S3A_')) return 'good';
  if (nodeId.startsWith('S3B_')) return 'bad';
  return null;
}

// Check if both world paths are complete and ready for finalization
export function canFinalize(narrativeState: NarrativeState): boolean {
  const segment3AComplete = getSegmentCompletion(narrativeState, '3A') === 'complete';
  const segment3BComplete = getSegmentCompletion(narrativeState, '3B') === 'complete';

  // Both Better World and Worse World must be complete
  return segment3AComplete && segment3BComplete;
}
